import { ReactElement, useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Button, Tooltip } from '@wordpress/components';
import { Icon, chevronRightSmall, drawerLeft, external } from '@wordpress/icons';
import { useTranslate } from 'i18n-calypso';
import clsx from 'clsx';

import useShellNav, { ShellNavItem } from './use-shell-nav';

export interface NavShortcut {
	/** As shown in the tooltip, e.g. "⌘B". */
	display: string;
	/** As announced, e.g. "Command B". */
	ariaLabel: string;
}

interface Props {
	id: string;
	/** Off-canvas state on narrow viewports; ignored by the layout on wide ones. */
	open: boolean;
	onClose: () => void;
	/** Icon rail (wide viewports only): labels hidden, each item named by a tooltip. */
	collapsed: boolean;
	onToggleCollapsed: () => void;
	/** The collapse toggle's keyboard shortcut, shown in its tooltip. */
	shortcut: NavShortcut;
}

export default function SideNav( { id, open, onClose, collapsed, onToggleCollapsed, shortcut }: Props ) {
	const translate = useTranslate();
	const nav = useShellNav();
	const { pathname } = useLocation();
	const ref = useRef< HTMLElement >( null );

	// Off-canvas mode: close after navigating, on Escape, and move focus in when opened.
	useEffect( () => {
		onClose();
	}, [ pathname, onClose ] );

	useEffect( () => {
		if ( ! open ) {
			return;
		}
		const onKeyDown = ( event: KeyboardEvent ) => {
			if ( event.key === 'Escape' ) {
				onClose();
			}
		};
		document.addEventListener( 'keydown', onKeyDown );
		ref.current?.querySelector< HTMLElement >( 'a, button' )?.focus();

		return () => document.removeEventListener( 'keydown', onKeyDown );
	}, [ open, onClose ] );

	return (
		<nav id={ id } ref={ ref } className={ clsx( 'app-shell__nav', { 'is-open': open } ) } aria-label={ nav.label }>
			<ul className="app-shell__nav-list">
				{ nav.items.map( item => <NavItem key={ item.key } item={ item } pathname={ pathname } collapsed={ collapsed } /> ) }
			</ul>
			{ nav.footer.length > 0 && (
				<ul className="app-shell__nav-list app-shell__nav-list--footer">
					{ nav.footer.map( item => <NavItem key={ item.key } item={ item } pathname={ pathname } collapsed={ collapsed } /> ) }
				</ul>
			) }
			{ nav.resources.length > 0 && (
				<ul className="app-shell__nav-list app-shell__nav-list--resources">
					{ nav.resources.map( item => <NavItem key={ item.key } item={ item } pathname={ pathname } collapsed={ collapsed } /> ) }
				</ul>
			) }
			<div className="app-shell__nav-collapse">
				<Button
					__next40pxDefaultSize
					className="app-shell__nav-collapse-button"
					icon={ drawerLeft }
					label={ String( collapsed ? translate( 'Expand sidebar' ) : translate( 'Collapse sidebar' ) ) }
					shortcut={ shortcut }
					showTooltip
					tooltipPosition="middle right"
					onClick={ onToggleCollapsed }
				/>
			</div>
		</nav>
	);
}

function NavItem( { item, pathname, collapsed }: { item: ShellNavItem; pathname: string; collapsed: boolean } ) {
	const hasChildren = !! item.children?.length && ! item.disabled;
	// Nested items are a route-driven disclosure: open while the parent or any child is current.
	const expanded = hasChildren && !! item.to && pathname.startsWith( item.to.split( '?' )[ 0 ] );

	return (
		<li className="app-shell__nav-item">
			<NavItemLink item={ item } expanded={ hasChildren ? expanded : undefined } collapsed={ collapsed } />
			{ hasChildren && expanded && (
				<ul className="app-shell__nav-list app-shell__nav-list--nested">
					{ item.children!.map( child => <NavItem key={ child.key } item={ child } pathname={ pathname } collapsed={ collapsed } /> ) }
				</ul>
			) }
		</li>
	);
}

function NavItemLink( { item, expanded, collapsed }: { item: ShellNavItem; expanded?: boolean; collapsed: boolean } ) {
	// A parent (`expanded` defined) never takes the highlight: its child does, and the open chevron plus
	// the revealed sub-items already show where you are. Otherwise Billing and Summary, which share a
	// route, would both light up.
	const isLeaf = expanded === undefined;
	const className = ( { isActive = false }: { isActive?: boolean } = {} ) =>
		clsx( 'app-shell__nav-link', { 'is-active': isActive && isLeaf } );
	const icon = item.icon && (
		<Icon icon={ item.icon } size={ 24 } className="app-shell__nav-icon" aria-hidden="true" />
	);
	const chevron = expanded !== undefined && (
		<Icon
			icon={ chevronRightSmall }
			size={ 20 }
			className={ clsx( 'app-shell__nav-chevron', { 'is-expanded': expanded } ) }
			aria-hidden="true"
		/>
	);
	// In the icon rail the label is visually hidden and a tooltip shows it. The link carries it as its
	// name too, which also keeps the tooltip from announcing it a second time as a description.
	const railLabel = collapsed ? item.label : undefined;
	const withTooltip = ( link: ReactElement ) =>
		collapsed ? <Tooltip text={ item.label } placement="right">{ link }</Tooltip> : link;

	// No tooltip: disabled items take no pointer events.
	if ( item.disabled ) {
		return (
			<span className={ className() } aria-disabled="true">
				{ icon }
				<span className="app-shell__nav-label">{ item.label }</span>
				{ chevron }
			</span>
		);
	}

	if ( item.href ) {
		const target = item.target ?? '_blank';

		return withTooltip(
			<a className={ className() } href={ item.href } target={ target } rel={ target === '_blank' ? 'noreferrer' : undefined } aria-label={ railLabel }>
				{ icon }
				<span className="app-shell__nav-label">{ item.label }</span>
				<Icon icon={ external } size={ 16 } className="app-shell__nav-external" aria-hidden="true" />
			</a>
		);
	}

	return withTooltip(
		<NavLink to={ item.to! } end={ item.end } className={ className } aria-label={ railLabel }>
			{ icon }
			<span className="app-shell__nav-label">{ item.label }</span>
			{ chevron }
		</NavLink>
	);
}
